import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ElementType,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { Icon } from './Icon';

const cx = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(' ');

/* ---------- Button ---------- */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'quiet' | 'danger' | 'inverse';
  size?: 'sm' | 'md' | 'lg';
  icon?: string;
  iconAfter?: string;
  block?: boolean;
  as?: ElementType;
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  iconAfter,
  block = false,
  disabled = false,
  as,
  className = '',
  ...rest
}: ButtonProps) {
  const Tag: ElementType = as || 'button';
  return (
    <Tag
      className={cx('zen-btn', `zen-btn--${variant}`, `zen-btn--${size}`, block && 'zen-btn--block', className)}
      disabled={Tag === 'button' ? disabled : undefined}
      aria-disabled={disabled || undefined}
      {...rest}
    >
      {icon && <Icon name={icon} size="1.15em" />}
      {children}
      {iconAfter && <Icon name={iconAfter} size="1.15em" />}
    </Tag>
  );
}

/* ---------- IconButton ---------- */
export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string;
  label: string;
  variant?: 'plain' | 'solid' | 'outline';
  size?: 'sm' | 'md' | 'lg';
}

export function IconButton({ icon, label, variant = 'plain', size = 'md', className = '', ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      className={cx('zen-iconbtn', `zen-iconbtn--${variant}`, `zen-iconbtn--${size}`, className)}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  );
}

/* ---------- Badge ---------- */
export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: 'neutral' | 'brand' | 'solid' | 'success' | 'warning' | 'danger';
  dot?: boolean;
}

export function Badge({ children, tone = 'neutral', dot = false, className = '', ...rest }: BadgeProps) {
  return (
    <span className={cx('zen-badge', `zen-badge--${tone}`, className)} {...rest}>
      {dot && <span className="zen-badge__dot" />}
      {children}
    </span>
  );
}

/* ---------- Avatar ---------- */
const AVATAR_SIZES: Record<string, number> = { sm: 28, md: 36, lg: 48, xl: 72 };

export interface AvatarProps extends HTMLAttributes<HTMLSpanElement> {
  name?: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | number;
}

export function Avatar({ name = '', src, size = 'md', className = '', ...rest }: AvatarProps) {
  const px = typeof size === 'number' ? size : AVATAR_SIZES[size] || 36;
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
  return (
    <span
      className={cx('zen-avatar', className)}
      style={{ width: px, height: px, fontSize: Math.round(px * 0.36) }}
      title={name || undefined}
      {...rest}
    >
      {src ? <img src={src} alt={name} /> : initials}
    </span>
  );
}

/* ---------- Tag ---------- */
export interface TagProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'onClick'> {
  selected?: boolean;
  onRemove?: (e: MouseEvent) => void;
  onClick?: (e: MouseEvent) => void;
}

export function Tag({ children, selected = false, onRemove, onClick, className = '', ...rest }: TagProps) {
  return (
    <span
      className={cx('zen-tag', selected && 'zen-tag--selected', onClick && 'zen-tag--clickable', className)}
      onClick={onClick}
      {...rest}
    >
      {children}
      {onRemove && (
        <span
          className="zen-tag__x"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(e);
          }}
        >
          <Icon name="x" size="12px" />
        </span>
      )}
    </span>
  );
}

/* ---------- Card ---------- */
export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: 'default' | 'flat' | 'raised' | 'brand' | 'soft';
  pad?: 'none' | 'sm' | 'md' | 'lg' | false;
  interactive?: boolean;
  title?: ReactNode;
  action?: ReactNode;
}

export function Card({
  children,
  variant = 'default',
  pad = 'md',
  interactive = false,
  title,
  action,
  className = '',
  ...rest
}: CardProps) {
  const padClass =
    pad === false || pad === 'none' ? '' : pad === 'md' ? 'zen-card__pad' : `zen-card__pad zen-card__pad--${pad}`;
  return (
    <div
      className={cx('zen-card', variant !== 'default' && `zen-card--${variant}`, interactive && 'zen-card--interactive', className)}
      {...rest}
    >
      {title && (
        <div className="zen-card__header">
          <span className="zen-card__title">{title}</span>
          {action}
        </div>
      )}
      <div className={padClass}>{children}</div>
    </div>
  );
}

/* ---------- ProgressBar ---------- */
export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  value?: number;
  max?: number;
  size?: 'md' | 'lg';
  color?: string;
}

export function ProgressBar({ value = 0, max = 100, size = 'md', color, className = '', ...rest }: ProgressBarProps) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div
      className={cx('zen-progress', size === 'lg' && 'zen-progress--lg', className)}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemax={max}
      {...rest}
    >
      <div className="zen-progress__bar" style={{ width: pct + '%', background: color }} />
    </div>
  );
}

/* ---------- SectionHeading ---------- */
export interface SectionHeadingProps {
  eyebrow?: ReactNode;
  title?: ReactNode;
  sub?: ReactNode;
  align?: 'left' | 'center';
  rule?: boolean;
}

export function SectionHeading({ eyebrow, title, sub, align = 'left', rule = true }: SectionHeadingProps) {
  return (
    <div className={cx('zen-sectionhead', align === 'center' && 'zen-sectionhead--center')}>
      {eyebrow && <span className="zen-sectionhead__eyebrow">{eyebrow}</span>}
      <h2 className="zen-sectionhead__title">{title}</h2>
      {rule && <hr className="zen-rule" />}
      {sub && <p className="zen-sectionhead__sub">{sub}</p>}
    </div>
  );
}

/* ---------- PetalField (decorative) ---------- */
export interface PetalFieldProps {
  size?: number;
  opacity?: number;
  blur?: number;
  style?: CSSProperties;
}

export function PetalField({ size = 320, opacity = 1, blur = 0, style }: PetalFieldProps) {
  const r = size * 0.34;
  const off = size * 0.16;
  const petals = [
    { top: 0, left: off },
    { top: off, left: 0 },
    { top: off, left: off * 2 },
    { top: off * 2, left: off },
  ];
  return (
    <div
      style={{ position: 'relative', width: size, height: size, opacity, filter: blur ? `blur(${blur}px)` : undefined, pointerEvents: 'none', ...style }}
      aria-hidden="true"
    >
      {petals.map((p, i) => (
        <span
          key={i}
          style={{ position: 'absolute', top: p.top, left: p.left, width: r * 2, height: r * 2, borderRadius: '50%', background: 'var(--petal-25)', mixBlendMode: 'multiply' }}
        />
      ))}
    </div>
  );
}
